/// <reference types="vite/client" />
import type { ReactNode } from "react";
import { useEffect } from "react";
import {
  createRootRouteWithContext,
  Outlet,
  useMatchRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/query-core";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useHydrateAtoms } from "jotai/utils";
import { queryClientAtom } from "jotai-tanstack-query";
import { googleLoginClientId } from "common/config";
import { registerSuperProperties } from "common/analytics";
import { setCurrentPageKey, getCurrentPageKey } from "@/analytics/pageKey";
import { useAnalyticsSuperProperties } from "@/analytics/superProps";
import { initWebAnalytics } from "@/analytics/init";
import Navbar from "@/features/home/Navbar";
import ScrollToTop from "react-scroll-to-top";
import NotFound from "@/features/not-found/page";
import { useDarkMode } from "@/hooks/useDarkMode";
import { GetInTouchModal } from "@/features/get-in-touch/GetInTouchModal";
import AuthModals from "@/features/auth/AuthModals";
import { ThemedToastContainer } from "@/lib/themed-toast-container";

import "../main.css";
import "common/styles/color-tokens.css";
import "common/styles/typography-tokens.css";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
        },
      ],
      links: [
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "192x192",
          href: "/android-chrome-192x192.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "512x512",
          href: "/android-chrome-512x512.png",
        },
        { rel: "shortcut icon", href: "/favicon.ico" },
        { rel: "manifest", href: "/manifest.json" },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap",
        },
      ],
      scripts: [
        {
          src: "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
          async: true,
        },
      ],
    }),
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
  },
);

function AtomsHydrator({ children }: { children: ReactNode }) {
  const queryClient = Route.useRouteContext({
    select: (ctx) => ctx.queryClient,
  });
  useHydrateAtoms([[queryClientAtom, queryClient]] as const);
  return <>{children}</>;
}

function RootComponent() {
  return (
    <RootDocument>
      <RootLayout />
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <HelmetProvider>
          <GoogleOAuthProvider clientId={googleLoginClientId}>
            <AtomsHydrator>
              {children}
              <AuthModals />
              <ThemedToastContainer />
            </AtomsHydrator>
          </GoogleOAuthProvider>
        </HelmetProvider>
        <Scripts />
      </body>
    </html>
  );
}

function RootLayout() {
  const isDark = useDarkMode();
  const matchRoute = useMatchRoute();
  const router = useRouter();

  // Keep the reactive super properties (dark_mode, auth_state, user_type) in sync.
  useAnalyticsSuperProperties();

  const isMySavedPlacesActive = matchRoute({
    to: "/savedplacedetails/$savedPlaceId/user/$userId",
  });
  const isMapActive = matchRoute({ to: "/" });
  const isVenuePhotosActive = matchRoute({ to: "/venuephotos/$venueSlug" });

  const useInnerScrollLayout = isMySavedPlacesActive || isMapActive;
  useEffect(() => {
    if (useInnerScrollLayout) {
      document.body.classList.add("app-inner-scroll");
    } else {
      document.body.classList.remove("app-inner-scroll");
    }
    return () => {
      document.body.classList.remove("app-inner-scroll");
    };
  }, [useInnerScrollLayout]);

  useEffect(() => {
    const handleRouteChange = () => {
      setTimeout(() => {
        const mainElement = document.getElementById("main");
        if (mainElement) {
          mainElement.setAttribute("tabindex", "-1");
          mainElement.focus();
          mainElement.addEventListener(
            "blur",
            () => {
              mainElement.removeAttribute("tabindex");
            },
            { once: true },
          );
        } else {
          const firstHeading = document.querySelector("h1");
          if (firstHeading) {
            const heading = firstHeading as HTMLElement;
            heading.setAttribute("tabindex", "-1");
            heading.focus();
            heading.addEventListener(
              "blur",
              () => {
                heading.removeAttribute("tabindex");
              },
              { once: true },
            );
          }
        }
      }, 100);
    };

    // Analytics: keep the `page_name` super property in sync with the resolved route.
    // Separate subscription so the a11y handler above stays analytics-free.
    const handlePageName = () => {
      setCurrentPageKey(router.state.location.pathname);
      registerSuperProperties({ page_name: getCurrentPageKey() });
    };
    initWebAnalytics(); // ensure the Mixpanel provider is registered on the client
    handlePageName(); // cold-load initial (onLoad may already have fired before mount)

    const unsubscribe = router.subscribe("onLoad", handleRouteChange);
    const unsubscribePageName = router.subscribe("onLoad", handlePageName);
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubscribePageName) {
        unsubscribePageName();
      }
    };
  }, [router]);

  return (
    <div
      className={`bg-bg-brand-secondary-default min-h-screen ${isMySavedPlacesActive || isMapActive ? "h-screen overflow-hidden" : ""}`}
    >
      {!isProduction && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-container-brand-default focus:text-fg-neutral-strong focus:rounded focus-blue"
      >
        Skip to content
      </a>
      {!isVenuePhotosActive && <Navbar />}
      <Outlet />
      {!isMapActive && (
        <ScrollToTop
          smooth
          aria-label="Scroll to top"
          component={
            <img
              src={
                isDark
                  ? "/images/BackToTop_Dark.svg"
                  : "/images/BackToTop_Light.svg"
              }
              alt="Scroll to top"
            />
          }
        />
      )}
      <GetInTouchModal />
    </div>
  );
}

function NotFoundComponent() {
  return <NotFound />;
}
